/**
 * How the top bar groups its entries. Pure, so it can be tested without a DOM.
 *
 * The page catalog (`access/pages.ts`) stays the source of WHICH screens a user may see — the
 * list reaching here is already filtered by role and portal capability. This only decides how
 * the survivors are ARRANGED: related screens under one heading, in workflow order, instead of
 * seventeen flat links wrapping across several rows.
 *
 *  · a group with one visible screen is just a link (no dropdown to open for a single item);
 *  · a screen not named in any group is kept, as a plain link at the end, so a page added to the
 *    catalog later shows up in the menu rather than silently vanishing from it.
 */
export interface NavItem { id: string; to: string; label: string }
export type NavEntry =
  | { kind: 'link'; id: string; to: string; label: string }
  | { kind: 'group'; label: string; items: NavItem[] };

export interface NavGroupDef { label: string; ids: readonly string[] }

/** Admin console. Order follows a working day: look, operate, sell, set up. */
export const ADMIN_GROUPS: readonly NavGroupDef[] = [
  { label: 'Dashboard', ids: ['A-01'] },
  { label: 'Operations', ids: ['A-14', 'A-26', 'A-18', 'A-43', 'A-40'] },
  { label: 'Retailers', ids: ['A-03', 'A-41'] },
  { label: 'Catalogue', ids: ['A-08', 'A-10'] },
  { label: 'Reports', ids: ['A-42', 'A-25'] },
  { label: 'Settings', ids: ['A-06', 'A-07', 'A-36', 'A-24', 'A-34'] },
];

export function groupNav(items: readonly NavItem[], groups: readonly NavGroupDef[]): NavEntry[] {
  const byId = new Map(items.map((i) => [i.id, i]));
  const placed = new Set<string>();
  const out: NavEntry[] = [];
  for (const g of groups) {
    const members = g.ids.map((id) => byId.get(id)).filter((i): i is NavItem => !!i);
    members.forEach((m) => placed.add(m.id));
    if (members.length === 0) continue;
    const [only] = members;
    if (members.length === 1 && only) out.push({ kind: 'link', ...only });
    else out.push({ kind: 'group', label: g.label, items: members });
  }
  for (const i of items) if (!placed.has(i.id)) out.push({ kind: 'link', ...i });
  return out;
}

/**
 * Does `pathname` sit at or under `route`, on a path-segment boundary? `/shop/orders` is NOT under
 * `/shop/order` — a bare `startsWith` said it was, and lit "Shop" and "Orders" together.
 */
export function underRoute(pathname: string, route: string): boolean {
  return pathname === route || pathname.startsWith(`${route}/`);
}

/** Screens that belong to a top-level entry though their route is not under it (an invoice opened from Orders). */
export const NAV_EXTRA_ROUTES: Readonly<Record<string, readonly string[]>> = {};
